// frontend/src/components/payments/PaymentMethodList.js
import React, { useState } from 'react';
import { 
  CreditCard, 
  Trash2, 
  Edit, 
  Shield, 
  CheckCircle, 
  AlertTriangle,
  Loader
} from 'lucide-react';

const PaymentMethodList = ({ paymentMethods, onDelete, onEdit }) => {
  const [deletingId, setDeletingId] = useState(null);

  const handleDelete = async (paymentMethodId) => {
    if (window.confirm('Are you sure you want to remove this payment method?')) {
      setDeletingId(paymentMethodId);
      try {
        await onDelete(paymentMethodId);
      } finally {
        setDeletingId(null);
      }
    }
  };

  const getCardIcon = (brand) => {
    const brandLower = brand?.toLowerCase();
    if (brandLower === 'visa') return '💳';
    if (brandLower === 'mastercard') return '💳';
    if (brandLower === 'amex') return '💳';
    if (brandLower === 'discover') return '💳';
    return '💳';
  };

  const formatCardNumber = (last4) => {
    return `•••• •••• •••• ${last4}`;
  };

  const getCardBrand = (brand) => {
    const brandLower = brand?.toLowerCase();
    if (brandLower === 'visa') return 'Visa';
    if (brandLower === 'mastercard') return 'Mastercard';
    if (brandLower === 'amex') return 'American Express';
    if (brandLower === 'discover') return 'Discover';
    return brand || 'Card';
  };

  if (paymentMethods.length === 0) {
    return (
      <div className="text-center py-12">
        <CreditCard className="w-12 h-12 text-ink-muted mx-auto mb-4" />
        <h3 className="text-lg font-medium text-ink-soft mb-2">No Payment Methods</h3>
        <p className="text-ink-muted mb-6">
          You haven't added any payment methods yet. Add one to get started.
        </p>
        <button
          onClick={onEdit}
          className="bg-accent text-white px-4 py-2 rounded-lg hover:bg-accent-hover flex items-center mx-auto"
        >
          <CreditCard className="w-4 h-4 mr-2" />
          Add Payment Method
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {paymentMethods.map((method) => (
        <div
          key={method.id}
          className="bg-surface-card border border-border-default rounded-lg p-4 transition-colors hover:border-border-strong"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="text-2xl">
                {getCardIcon(method.card_brand)}
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <span className="font-medium text-ink-soft">
                    {getCardBrand(method.card_brand)}
                  </span>
                  {method.card_last_four && (
                    <span className="text-ink-muted">
                      {formatCardNumber(method.card_last_four)}
                    </span>
                  )}
                  {method.is_default && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-small font-medium bg-success-tint text-success">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Default
                    </span>
                  )}
                </div>
                <div className="text-sm text-ink-muted mt-1">
                  Expires {method.card_exp_month}/{method.card_exp_year}
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => onEdit(method)}
                className="p-2 text-ink-muted hover:text-accent hover:bg-accent-tint rounded-lg transition-colors"
                title="Edit payment method"
              >
                <Edit className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleDelete(method.id)}
                disabled={deletingId === method.id}
                className="p-2 text-ink-muted hover:text-danger hover:bg-danger-tint rounded-lg transition-colors disabled:opacity-50"
                title="Remove payment method"
              >
                {deletingId === method.id ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        </div>
      ))}
      
      {/* Security notice */}
      <div className="bg-info-tint border border-info-border rounded-lg p-4">
        <div className="flex items-start">
          <Shield className="w-5 h-5 text-accent mt-0.5 mr-3" />
          <div className="text-sm text-info">
            <div className="font-medium mb-1">Secure Payment Processing</div>
            <div>
              Your payment information is encrypted and securely stored by Stripe. 
              We never store your full card details on our servers.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodList;
